import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from './ui/accordion';
import {
  Loader2, FolderCheck, FolderSearch, FolderX, ListChecks,
  CheckCircle2, ArrowUpRight, ClipboardList, AlertTriangle,
  Play, RefreshCw, Lock, ShieldCheck, ShieldAlert, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEM_COLORS = { verde: '#16a34a', ambar: '#f59e0b', rojo: '#dc2626', desconocido: '#94a3b8' };
const SEM_LABEL = { verde: 'En plazo', ambar: 'Atención', rojo: 'Riesgo', desconocido: 'N/D' };

const Kpi = ({ icon: Icon, label, value, accent, testid, onClick }) => (
  <Card className={`${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
        onClick={onClick} data-testid={testid}>
    <CardContent className="py-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 truncate">{label}</p>
        <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const ExpLink = ({ item, navigate, onAnalizar, analizando }) => {
  const esError = item.motivo === 'error';
  const enProceso = item.motivo === 'en_proceso';
  return (
    <div
      className="group flex items-center justify-between gap-2 w-full px-3 py-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors"
      data-testid={`exp-row-${item.expediente_id}`}
    >
      <button
        onClick={() => navigate(`/expedientes/${item.expediente_id}`)}
        className="flex items-center gap-2 min-w-0 flex-1 text-left"
        data-testid={`exp-link-${item.expediente_id}`}
      >
        <span className="text-sm font-medium text-slate-800 truncate">{item.numero_expediente}</span>
        {item.cerrado_usuario && (
          <Badge className="bg-slate-200 text-slate-700 flex items-center gap-1 shrink-0" data-testid={`badge-cerrado-${item.expediente_id}`}>
            <Lock className="w-3 h-3" /> Cerrado
          </Badge>
        )}
        {esError && (
          <Badge className="bg-red-100 text-red-700 shrink-0" title={item.error_msg || 'La auditoría falló'}>Error</Badge>
        )}
        {enProceso && <Badge className="bg-blue-100 text-blue-700 shrink-0">En proceso</Badge>}
        {item.tipo_procedimiento && (
          <span className="text-xs text-slate-500 truncate hidden sm:inline">· {item.tipo_procedimiento}</span>
        )}
        <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 shrink-0 ml-1" />
      </button>
      {onAnalizar && (
        <Button
          size="sm" variant="outline" className="shrink-0 h-7 px-2"
          disabled={analizando === 'loading' || analizando === 'done' || enProceso}
          onClick={(e) => { e.stopPropagation(); onAnalizar(item); }}
          data-testid={`analizar-btn-${item.expediente_id}`}
        >
          {analizando === 'loading'
            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Lanzando…</>
            : analizando === 'done'
              ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" /> En cola</>
              : esError
                ? <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reintentar</>
                : <><Play className="w-3.5 h-3.5 mr-1" /> Analizar</>}
        </Button>
      )}
    </div>
  );
};

export default function EstadisticasAuditoria() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analizando, setAnalizando] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auditorias-procesales/estadisticas/resumen`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const lanzarAnalisis = async (item) => {
    setAnalizando((s) => ({ ...s, [item.expediente_id]: 'loading' }));
    try {
      await axios.post(`${API}/auditorias-procesales/sesiones`, {
        nombre: `Análisis ${item.numero_expediente}`,
        modo: 'single',
        expediente_id: item.expediente_id,
        modelo_provider: 'gemini',
        modelo_name: 'gemini-3.1-pro-preview',
      });
      setAnalizando((s) => ({ ...s, [item.expediente_id]: 'done' }));
      toast.success(`Auditoría de ${item.numero_expediente} lanzada. Se procesa en segundo plano.`);
    } catch (e) {
      setAnalizando((s) => ({ ...s, [item.expediente_id]: undefined }));
      toast.error(e.response?.data?.detail || 'No se pudo lanzar la auditoría');
    }
  };

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400" data-testid="estadisticas-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Calculando estadísticas...
      </div>
    );
  }
  if (!data) return null;

  const pctAnalizados = data.total_expedientes
    ? Math.round((data.analizados / data.total_expedientes) * 100) : 0;

  const semData = Object.entries(data.por_semaforo || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: SEM_LABEL[k] || k, key: k, value: v }));

  const tramiteChart = (data.por_tramite || []).slice(0, 8).map((t) => ({
    name: t.tramite.length > 32 ? t.tramite.slice(0, 32) + '…' : t.tramite,
    value: t.count,
  }));

  return (
    <div className="space-y-6" data-testid="estadisticas-auditoria">
      {/* Banner de cobertura de auditoría */}
      {(() => {
        const cob = data.cobertura || {};
        const completo = cob.completo;
        return (
          <Card className={`border-l-4 ${completo ? 'border-l-green-500 bg-green-50/50' : 'border-l-amber-500 bg-amber-50/50'}`}
                data-testid="cobertura-banner">
            <CardContent className="py-4 flex items-start gap-3">
              {completo
                ? <ShieldCheck className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
                : <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800" data-testid="cobertura-titulo">
                  {completo
                    ? `Cobertura completa: los ${cob.total} expedientes están auditados.`
                    : `Cobertura ${cob.pct}% — faltan ${cob.sin_analizar} de ${cob.total} expedientes por auditar.`}
                </p>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Auditados: {cob.auditados}</span>
                  {cob.nunca_analizados > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="cob-nunca">Nunca analizados: {cob.nunca_analizados}</span>
                  )}
                  {cob.con_error > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="cob-error">Con error (relanzar): {cob.con_error}</span>
                  )}
                  {cob.en_proceso > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid="cob-proceso">En proceso: {cob.en_proceso}</span>
                  )}
                  {data.cerrados > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Cerrados por el usuario: {data.cerrados}
                    </span>
                  )}
                </div>
                {!completo && (
                  <p className="text-xs text-slate-500 mt-1.5">
                    Los pendientes se listan abajo en <b>“Sin analizar”</b>; pulsa <b>Analizar</b> (o <b>Reintentar</b> si dieron error) para completarlos.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={ListChecks} label="Expedientes totales" value={data.total_expedientes}
             accent="bg-slate-100 text-slate-700" testid="kpi-total" />
        <Kpi icon={FolderCheck} label="Analizados" value={data.analizados}
             accent="bg-blue-100 text-blue-700" testid="kpi-analizados" />
        <Kpi icon={FolderSearch} label="Sin analizar" value={data.sin_analizar}
             accent="bg-amber-100 text-amber-700" testid="kpi-sin-analizar" />
        <Kpi icon={CheckCircle2} label="Finalizados" value={data.finalizados}
             accent="bg-green-100 text-green-700" testid="kpi-finalizados" />
        <Kpi icon={ClipboardList} label="Pendientes de trámite" value={data.pendientes}
             accent="bg-red-100 text-red-700" testid="kpi-pendientes" />
      </div>

      {/* Progreso de análisis */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">Progreso de análisis</p>
            <span className="text-sm text-slate-500" data-testid="progreso-pct">
              {data.analizados}/{data.total_expedientes} ({pctAnalizados}%)
            </span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${pctAnalizados}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Gráficos */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="py-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">Reparto por semáforo</p>
            {semData.length === 0 ? (
              <p className="text-sm text-slate-400 py-10 text-center">Sin datos analizados todavía.</p>
            ) : (
              <div className="h-64" data-testid="chart-semaforo">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={semData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                         innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {semData.map((d) => <Cell key={d.key} fill={SEM_COLORS[d.key] || '#94a3b8'} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="flex flex-wrap gap-3 justify-center mt-1">
              {semData.map((d) => (
                <span key={d.key} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: SEM_COLORS[d.key] }} />
                  {d.name} ({d.value})
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">Trámites pendientes más frecuentes</p>
            {tramiteChart.length === 0 ? (
              <p className="text-sm text-slate-400 py-10 text-center">No hay trámites pendientes.</p>
            ) : (
              <div className="h-64" data-testid="chart-tramites">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tramiteChart} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pendientes por trámite (agrupado) */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-semibold text-slate-700">Expedientes pendientes agrupados por trámite</p>
          </div>
          {(data.por_tramite || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No hay trámites pendientes. 🎉</p>
          ) : (
            <Accordion type="multiple" className="w-full" data-testid="acc-tramites">
              {data.por_tramite.map((t, i) => (
                <AccordionItem key={i} value={`t-${i}`} data-testid={`tramite-item-${i}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <Badge className="bg-amber-100 text-amber-800 shrink-0">{t.count}</Badge>
                      <span className="text-sm text-slate-700">{t.tramite}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1 pt-1">
                      {t.expedientes.map((e) => (
                        <ExpLink key={e.expediente_id} item={e} navigate={navigate} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Finalizados y sin analizar */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <p className="text-sm font-semibold text-slate-700">Finalizados (todo cobrado)</p>
              <Badge className="bg-green-100 text-green-800 ml-auto">{data.finalizados}</Badge>
            </div>
            {data.expedientes_finalizados.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Ningún expediente finalizado todavía.</p>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="lista-finalizados">
                {data.expedientes_finalizados.map((e) => (
                  <ExpLink key={e.expediente_id} item={e} navigate={navigate} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <FolderX className="w-4 h-4 text-amber-600" />
              <p className="text-sm font-semibold text-slate-700">Sin analizar</p>
              <Badge className="bg-amber-100 text-amber-800 ml-auto">{data.sin_analizar}</Badge>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={cargar}
                      data-testid="refrescar-estadisticas-btn" title="Actualizar estadísticas">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
            {data.expedientes_sin_analizar.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Todos los expedientes están analizados. ✅</p>
            ) : (
              <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="lista-sin-analizar">
                {data.expedientes_sin_analizar.map((e) => (
                  <ExpLink key={e.expediente_id} item={e} navigate={navigate}
                           onAnalizar={lanzarAnalisis} analizando={analizando[e.expediente_id]} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
